import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setCookie } from "../../utils/common";
import {
  checkPassword,
  fetchLoginIdentifierLinks,
  selectLoginIdentifier,
} from "../../services/login";
import { useUser } from "../../context/UserContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Eye, EyeOff, X } from "lucide-react";
import Buildvizer from '../../assets/images/Buildvizer.png';
import start_tower from "../../assets/images/stark_tower.jpg";
import { Form, Field, FormSubmit } from "../../components/form";
import { loginSchema } from "../../services/login/schemas";
import CustomerPickerModal from "../../components/CustomerPickerModal";

// Persona → landing route. STAFF / DEVELOPER_ADMIN / ZUMALE_USER fall through
// to the admin dashboard; everyone else has a dedicated page.
const PERSONA_LANDING = {
  INTERIOR_DESIGNER: "/interior-designer/leads",
  BV_ASSOCIATE: "/bv-user/buyers",
  CHANNEL_PARTNER: "/agent-dashboard",
  PROPERTY_BUYER: "/user-dashboard",
  ZUMALE_USER: "/zumale_user-dashboard",
  ARCHITECT: "/architect/dashboard",
  FREELANCER: "/freelancer/dashboard",
};
const landingFor = (persona) => PERSONA_LANDING[persona] || "/admin-dashboard";

function LoginPage() {
  const navigate = useNavigate();
  const { setUserInfo } = useUser();
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupDrawer, setShowSignupDrawer] = useState(false);

  // Customer-picker state — populated when /login/identifier-links returns
  // more than one link. Stays empty for the common single-org case.
  const [pickerLinks, setPickerLinks] = useState([]);
  const [pickerPersona, setPickerPersona] = useState(null);
  const [pendingUserInfo, setPendingUserInfo] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = showSignupDrawer ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [showSignupDrawer]);

  // Finalize login: persist token + userInfo, then navigate. The optional
  // `links` arg caches the identifier-links list on userInfo so the topbar
  // chip can decide its multi-org affordance without an extra GET.
  const finalizeLogin = (userInfo, links) => {
    const persisted = {
      ...userInfo,
      identifierLinks: Array.isArray(links)
        ? links
        : Array.isArray(userInfo?.links)
          ? userInfo.links
          : [],
    };
    setCookie("_USER_AUTH_", persisted);
    setCookie("accessToken", persisted.accessToken, { path: "/", expires: 1 });
    setUserInfo(persisted);
    navigate(landingFor(persisted.persona));
  };

  const handleLogin = async ({ email, password }) => {
    try {
      const userInfo = await checkPassword(email, password);
      if (!userInfo?.loginId || !userInfo?.persona || !userInfo?.accessToken) {
        toast.error("Invalid email or password.");
        return;
      }
      // The axios interceptor pulls the bearer from the `accessToken` cookie,
      // so we MUST set the cookie before firing the identifier-links GET.
      // We also stash the temp userInfo so we can finalize without a second
      // checkPassword if the picker is dismissed in an edge case.
      setCookie("_USER_AUTH_", userInfo);
      setCookie("accessToken", userInfo.accessToken, { path: "/", expires: 1 });

      let links = null;
      try {
        const linksResp = await fetchLoginIdentifierLinks();
        links = linksResp?.links || [];
      } catch (linkErr) {
        // Don't block login on a links lookup failure — fall back to single-
        // org behavior using whatever check-password returned.
        console.warn("identifier-links lookup failed; continuing single-org:", linkErr);
      }

      if (Array.isArray(links) && links.length > 1) {
        // Multi-org login — open the picker. Defer setUserInfo + navigate
        // until the user confirms.
        setPickerLinks(links);
        setPickerPersona(userInfo.persona);
        setPendingUserInfo(userInfo);
        setShowPicker(true);
        return;
      }

      // Single-org happy path (or links lookup unavailable). Cache whatever
      // links we did manage to fetch so the topbar chip can read them
      // without firing another GET.
      finalizeLogin(userInfo, links || []);
    } catch (err) {
      console.error("Error during login:", err);
      toast.error(err?.detail || err?.response?.data?.detail || "An error occurred during login.");
    }
  };

  const handlePickerConfirm = async ({ linkId, remember }) => {
    try {
      const fresh = await selectLoginIdentifier({ linkId, remember });
      if (!fresh?.accessToken || !fresh?.persona) {
        toast.error("Failed to switch to the selected organization.");
        return;
      }
      // Prefer links from the fresh response (BE includes them on switch);
      // fall back to the ones we already showed in the picker so the topbar
      // chip stays multi-org-aware even if the BE omits them.
      const linksForCache = Array.isArray(fresh.links) ? fresh.links : pickerLinks;
      setShowPicker(false);
      setPickerLinks([]);
      setPendingUserInfo(null);
      finalizeLogin(fresh, linksForCache);
    } catch (err) {
      console.error("Error selecting login identifier:", err);
      toast.error(
        err?.detail || err?.response?.data?.detail || "Could not switch organization."
      );
    }
  };

  const togglePasswordVisibility = () => setShowPassword((s) => !s);

  const handleSignupOptionClick = (role) => {
    if (role === 'interior-designer') {
      navigate('/signup/interior-designer');
      setShowSignupDrawer(false);
    } else if (role === 'architect') {
      navigate('/signup/architect');
      setShowSignupDrawer(false);
    } else if (role === 'freelancer') {
      navigate('/signup/freelancer');
      setShowSignupDrawer(false);
    } else if (role === 'channel-partner') {
      // Show coming soon message - don't navigate
      toast.info("Channel Partner signup is coming soon!");
    }
  };

  return (
    <div className="relative isolate min-h-dvh w-full bg-slate-100">
      {/* Full-viewport layer: avoids gray bars (padding on a different layer; image not tied to content box height) */}
      <div
        className="pointer-events-none fixed inset-0 z-0 bg-slate-100 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${start_tower})` }}
        aria-hidden
      />
      <div className="relative z-10 flex min-h-dvh w-full justify-end items-center p-16">
      {/* Main Login Card */}
      <div className="flex rounded-md bg-white shadow-lg max-w-lg w-1/4 overflow-hidden backdrop-blur-lg bg-opacity-70 relative">
        <div className="w-full p-8">
          <div className="flex justify-center mb-6">
            <img
              src={Buildvizer}
              alt="Company Logo"
              className="h-32 w-auto"
            />
          </div>

          <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">
            Login
          </h2>

          <Form
            schema={loginSchema}
            defaultValues={{ email: "", password: "" }}
            onSubmit={handleLogin}
            className="space-y-4"
          >
            <Field name="email" label="Email" placeholder="Enter Email" required />

            <Field
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter Password"
              required
              endAdornment={
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              }
            />

            <FormSubmit loadingLabel="Signing in..." block>Login</FormSubmit>
          </Form>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => setShowSignupDrawer(true)}
              className="w-full flex items-center justify-center py-2.5 px-4 border-2 border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:border-flamingo-400 hover:bg-flamingo-50 transition-colors"
            >
              Sign up
            </button>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {showSignupDrawer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={() => setShowSignupDrawer(false)}
        />
      )}

      {/* Side Drawer */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out ${
          showSignupDrawer ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Drawer Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800">Sign Up</h3>
            <button
              onClick={() => setShowSignupDrawer(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Drawer Content */}
          <div className="flex-1 p-6">
            <p className="text-sm text-gray-600 mb-6">
              Select your role to continue with signup
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => handleSignupOptionClick('interior-designer')}
                className="w-full text-left px-6 py-4 border-2 border-gray-200 rounded-lg hover:border-flamingo-400 hover:bg-flamingo-50 transition-all duration-200 group"
              >
                <h4 className="font-semibold text-gray-800 group-hover:text-flamingo-600 mb-1">
                  Interior Designer
                </h4>
                <p className="text-sm text-gray-500">
                  Create designs and manage projects
                </p>
              </button>

              <button
                onClick={() => handleSignupOptionClick('architect')}
                className="w-full text-left px-6 py-4 border-2 border-gray-200 rounded-lg hover:border-flamingo-400 hover:bg-flamingo-50 transition-all duration-200 group"
              >
                <h4 className="font-semibold text-gray-800 group-hover:text-flamingo-600 mb-1">
                  Architect
                </h4>
                <p className="text-sm text-gray-500">
                  Delegate drafting &amp; detailing to trusted talent
                </p>
              </button>

              <button
                onClick={() => handleSignupOptionClick('freelancer')}
                className="w-full text-left px-6 py-4 border-2 border-gray-200 rounded-lg hover:border-flamingo-400 hover:bg-flamingo-50 transition-all duration-200 group"
              >
                <h4 className="font-semibold text-gray-800 group-hover:text-flamingo-600 mb-1">
                  Freelancer
                </h4>
                <p className="text-sm text-gray-500">
                  Grow your architecture career with paid projects
                </p>
              </button>

              <div className="relative">
                <div className="w-full text-left px-6 py-4 border-2 border-gray-200 rounded-lg bg-gray-50 opacity-75 cursor-not-allowed">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-600 mb-1">
                        Channel Partner
                      </h4>
                      <p className="text-sm text-gray-400">
                        Partner with us to grow your business
                      </p>
                    </div>
                    <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded">
                      Coming Soon
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleSignupOptionClick('channel-partner')}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  aria-label="Channel Partner - Coming Soon"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Multi-org picker — non-dismissable; only renders when the BE
          returned >1 identifier link for the just-authenticated login. */}
      <CustomerPickerModal
        isOpen={showPicker}
        onClose={() => { /* initial mode is non-dismissable */ }}
        links={pickerLinks}
        mode="initial"
        persona={pickerPersona || pendingUserInfo?.persona}
        onConfirm={handlePickerConfirm}
      />
    </div>
  );
}

export default LoginPage;
